/**
 * AgentMiniWidget — Floating embedded agent progress overlay
 *
 * Shows active agent runs as glassmorphism cards anchored to
 * the bottom-right of the editor. Supports:
 * - Collapsed: avatar + name + status pulse + 1-line summary
 * - Expanded: mini transcript preview + "Open full chat" link
 * - Multiple runs stacked vertically
 * - Slide-in / slide-out animations
 * - Mobile-friendly touch targets
 */
import { useMemo } from 'react'
import { useEmbeddedAgents, type EmbeddedAgentRun } from '../contexts/EmbeddedAgentContext'
import './AgentMiniWidget.css'

interface AgentMiniWidgetProps {
    onOpenAgent?: (agentPath: string) => void
}

export function AgentMiniWidget({ onOpenAgent }: AgentMiniWidgetProps) {
    const { runs, stopRun, dismissRun, toggleExpanded } = useEmbeddedAgents()

    if (runs.length === 0) return null

    return (
        <div className="agent-mini-stack">
            {runs.map((run, index) => (
                <AgentMiniCard
                    key={run.id}
                    run={run}
                    index={index}
                    onStop={() => stopRun(run.id)}
                    onDismiss={() => dismissRun(run.id)}
                    onToggle={() => toggleExpanded(run.id)}
                    onOpenFull={() => onOpenAgent?.(run.transcriptPath)}
                />
            ))}
        </div>
    )
}

// ────────────────────────────────────────────────────────────────
// Single card
// ────────────────────────────────────────────────────────────────

interface CardProps {
    run: EmbeddedAgentRun
    index: number
    onStop: () => void
    onDismiss: () => void
    onToggle: () => void
    onOpenFull: () => void
}

function AgentMiniCard({ run, index, onStop, onDismiss, onToggle, onOpenFull }: CardProps) {
    const statusClass = useMemo(() => {
        switch (run.status) {
            case 'running': return 'status-running'
            case 'stopping': return 'status-stopping'
            case 'completed': return 'status-completed'
            case 'error': return 'status-error'
            case 'cancelled': return 'status-cancelled'
            default: return ''
        }
    }, [run.status])

    const isActive = run.status === 'running' || run.status === 'stopping'

    // Last few transcript events for expanded preview
    const previewEvents = useMemo(() => {
        return run.transcript
            .filter(e => e.type === 'assistant_message_final' || e.type === 'tool_call' || e.type === 'error')
            .slice(-3)
    }, [run.transcript])

    return (
        <div
            className={`agent-mini-card ${statusClass} ${run.expanded ? 'expanded' : ''}`}
            style={{ '--card-index': index } as React.CSSProperties}
        >
            {/* Collapsed header — always visible */}
            <div className="agent-mini-header" onClick={onToggle}>
                <div className={`agent-mini-pulse ${statusClass}`} />
                <span className="agent-mini-avatar">🤖</span>
                <div className="agent-mini-info">
                    <span className="agent-mini-name">{run.agentName}</span>
                    <span className="agent-mini-action">{run.latestAction}</span>
                </div>
                <div className="agent-mini-controls">
                    {isActive && (
                        <button
                            className="agent-mini-btn agent-mini-stop"
                            onClick={e => { e.stopPropagation(); onStop() }}
                            title="Stoppa"
                        >
                            ⏹
                        </button>
                    )}
                    <button
                        className="agent-mini-btn agent-mini-dismiss"
                        onClick={e => { e.stopPropagation(); onDismiss() }}
                        title="Stäng"
                    >
                        ✕
                    </button>
                </div>
            </div>

            {/* Expanded content */}
            {run.expanded && (
                <div className="agent-mini-expanded">
                    {/* Streaming text preview */}
                    {run.streamingText && (
                        <div className="agent-mini-streaming">
                            <span className="streaming-cursor" />
                            {run.streamingText.slice(-200)}
                        </div>
                    )}

                    {/* Last few events */}
                    {previewEvents.map((evt, i) => (
                        <div key={i} className={`agent-mini-event event-${evt.type}`}>
                            {evt.type === 'assistant_message_final' && (
                                <p className="event-text">{(evt as Extract<typeof evt, { type: 'assistant_message_final' }>).text.slice(0, 120)}{(evt as Extract<typeof evt, { type: 'assistant_message_final' }>).text.length > 120 ? '…' : ''}</p>
                            )}
                            {evt.type === 'tool_call' && (
                                <p className="event-tool">🔧 {(evt as Extract<typeof evt, { type: 'tool_call' }>).name}</p>
                            )}
                            {evt.type === 'error' && (
                                <p className="event-error">❌ {(evt as Extract<typeof evt, { type: 'error' }>).message.slice(0, 80)}</p>
                            )}
                        </div>
                    ))}

                    {/* Cost info */}
                    {run.currentCost.estimatedCostUsd > 0 && (
                        <div className="agent-mini-cost">
                            💰 ${run.currentCost.estimatedCostUsd.toFixed(4)}
                        </div>
                    )}

                    {/* Open full chat button */}
                    <button className="agent-mini-open-full" onClick={onOpenFull}>
                        Öppna full chatt ↗
                    </button>
                </div>
            )}
        </div>
    )
}
